import { FC } from 'react';
import { Select } from '@doda/io-react/pc';
import { INCOME } from '@doda/common/src/constants/screenStructure/searchConditionItems';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { canSearchSalary } from '@doda/common/src/logics/validation';
import { INCOME_VALUE_LIST } from 'constants/searchCondition';
import styles from './index.module.scss';

/**
 * SearchIncomeSelectコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface Props {
  /** 上限を示すフラグ */
  isUpper: boolean;
  /** デフォルト値 */
  defaultValue: string;
  /** 上限年収 */
  upperIncome: string;
  /** 下限年収 */
  lowerIncome: string;
  /** 年収をセットする関数 */
  setIncome: (value: string) => void;
  /** 年収範囲エラーをセットする関数 */
  setIsIncomeRangeError: () => void;
  /** 年収変更時のコールバック関数 */
  onChange: (value: string) => void;
  /** 求人数を更新する関数 */
  updateJobCount: (currentCondition?: JobCountQuery) => Promise<void>;
}

const DEFAULT_LABEL = '指定しない';

const incomeOptionList = INCOME.list.map((item) => {
  const label = item.name === '未設定' ? DEFAULT_LABEL : item.name;
  return { label, value: item.id };
});

/**
 * SearchIncomeSelectは年収の検索条件設定を行うコンポーネント
 *
 * @param {Props} props - プロパティ
 * @returns {JSX.Element} - 年収検索条件設定の要素
 */
export const SearchIncomeSelect: FC<Props> = ({
  isUpper,
  defaultValue,
  upperIncome,
  lowerIncome,
  setIncome,
  setIsIncomeRangeError,
  onChange,
  updateJobCount,
}) => {
  const onChangeSelectBox = (value: string) => {
    // 年収バリデーションエラーの場合は早期リターン
    if (isUpper && !canSearchSalary(Number(lowerIncome), Number(value))) {
      setIsIncomeRangeError();
      setIncome(value);
      return;
    }
    if (!isUpper && !canSearchSalary(Number(value), Number(upperIncome))) {
      setIsIncomeRangeError();
      setIncome(value);
      return;
    }

    onChange(value);
    const limitIncome = INCOME_VALUE_LIST.find(
      (income) => income.id === value
    )?.value;

    if (isUpper) {
      updateJobCount({ upperLimitIncome: limitIncome });
    } else {
      updateJobCount({ lowerLimitIncome: limitIncome });
    }
  };

  const selectedOption = INCOME.list.find(
    (option) => option.id === defaultValue
  );
  const selectedName = selectedOption ? selectedOption.name : DEFAULT_LABEL;

  return (
    <div className={styles.searchIncomeSelect__wrapper}>
      <div className={styles.searchIncomeSelect__form}>
        <Select
          onChange={onChangeSelectBox}
          selectedOption={{
            value: defaultValue,
            label: selectedName === '未設定' ? DEFAULT_LABEL : selectedName,
          }}
          options={incomeOptionList}
          className={styles.searchIncomeSelect__select}
        />
      </div>
      <div className={styles.searchIncomeSelect__text}>
        {isUpper ? '以下' : '～'}
      </div>
    </div>
  );
};
